import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Images, Laugh, Pin, PinOff, Send, X } from "lucide-react";
import { useAuth, useMessage } from "../../global-components/src";
import { REFLEXO_API_URL, BUCKET_REFLEXO_URL } from "../../global-components/src/utils/vars";
import { clearText, errorMessage } from "../../global-components/src/utils/text";
import { getImageUser } from "../../global-components/src/utils/auth";
import { getNativeKeyboardHeight, getNativeNavigationHeight, isWebView } from "../../utils/platform";
import { convertHeic, compressImage } from "../../global-components/src/utils/file";
import EmojiPickerNative from "../../components/Textarea/Emojis/emoji-picker-native";
import { ReelComment } from "../../types";
import { useCommentEcho, CommentMutation } from "../../hooks/useCommentEcho";
import "./flow.css";
import { formatUploadBytes, type ResumableVideoUploadProgress, uploadResumableVideo } from "../../utils/resumable-video-upload";

interface CommentFile {
    file: File;
    src: string;
    type: "image" | "video";
}

interface ReelCommentMedia {
    id: number | string;
    path: string;
    type: number | string;
}

const sortPinnedComments = (comments: ReelComment[], pinnedIds: number[]) => {
    const pinned = new Set(pinnedIds);
    return [...comments].sort((a, b) => Number(pinned.has(b.id)) - Number(pinned.has(a.id)));
};

const mediaUrl = (path?: string | null) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    return `${BUCKET_REFLEXO_URL.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
};

function timeAgo(dateStr: string, t: (key: string, options?: any) => string) {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return t("time.justNow");
    if (diff < 3600) return t("time.minute", { count: Math.floor(diff / 60) });
    if (diff < 86400) return t("time.hour", { count: Math.floor(diff / 3600) });
    return t("time.day", { count: Math.floor(diff / 86400) });
}

const isVideoMedia = (media: ReelCommentMedia) => media.type === 1 || media.type === "video";

export default function CommentsDrawer({
    reelId,
    total,
    onClose,
}: {
    reelId: number;
    total: number;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const { token, user } = useAuth();
    const { setMessage } = useMessage();
    const [comments, setComments] = useState<ReelComment[]>([]);
    const [commentCount, setCommentCount] = useState(total);
    const [pinnedCommentIds, setPinnedCommentIds] = useState<number[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [text, setText] = useState("");
    const [gifUrl, setGifUrl] = useState("");
    const [selectedFiles, setSelectedFiles] = useState<CommentFile[]>([]);
    const [sending, setSending] = useState(false);
    const [videoUpload, setVideoUpload] = useState<ResumableVideoUploadProgress | null>(null);
    const videoUploadAbortRef = useRef<AbortController | null>(null);
    const [keyboardPadding, setKeyboardPadding] = useState(0);
    const [showEmoji, setShowEmoji] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useCommentEcho({
        token,
        reelId,
        onComment: (mutation: CommentMutation) => {
            const incomingId = Number(mutation.comment?.id);
            if (!incomingId) return;
            setComments(previous => {
                if (mutation.action === "deleted") return previous.filter(comment => Number(comment.id) !== incomingId);
                const incoming = mutation.comment as ReelComment;
                if (previous.some(comment => Number(comment.id) === incomingId)) {
                    return previous.map(comment => Number(comment.id) === incomingId ? { ...comment, ...incoming } : comment);
                }
                return sortPinnedComments([incoming, ...previous], pinnedCommentIds);
            });
            if (mutation.action === "created" && Number(mutation.comment?.id_user) !== Number(user?.id)) {
                setCommentCount(count => count + 1);
            }
        },
    });

    const headers = () => ({
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
    });

    const loadComments = async (nextPage: number) => {
        if (loading) return;
        setLoading(true);
        try {
            const { data } = await axios.get(`${REFLEXO_API_URL}/reels/${reelId}/comments?page=${nextPage}`, {
                headers: headers(),
            });
            const responsePinnedIds = Array.isArray(data.pinned_comment_ids)
                ? data.pinned_comment_ids.map(Number).filter(Number.isFinite)
                : pinnedCommentIds;
            setPinnedCommentIds(responsePinnedIds);
            setComments((prev) => sortPinnedComments(nextPage === 1 ? data.data : [...prev, ...data.data], responsePinnedIds));
            setHasMore(data.current_page < data.last_page);
            setPage(nextPage);
        } catch {
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setComments([]);
        setCommentCount(total);
        setPage(1);
        setHasMore(true);
        setPinnedCommentIds([]);
        loadComments(1);
        // A mudança do reel é a fonte de verdade desta lista.
    }, [reelId, total]);

    useEffect(() => {
        const adjustKeyboardPadding = () => {
            const heightKeyboard = getNativeKeyboardHeight();
            const nativeNavigationHeight = getNativeNavigationHeight() ?? 0;
            setKeyboardPadding(heightKeyboard ? heightKeyboard + nativeNavigationHeight : 0);
        };
        if (isWebView()) adjustKeyboardPadding();
        window.addEventListener("keyboardHeightChange", adjustKeyboardPadding);
        return () => window.removeEventListener("keyboardHeightChange", adjustKeyboardPadding);
    }, []);

    const handleScroll = () => {
        const el = listRef.current;
        if (!el || loading || !hasMore) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
            loadComments(page + 1);
        }
    };

    const handleMediaChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        if (!files.length) return;

        const processed: CommentFile[] = [];
        for (let file of files) {
            file = await convertHeic(file);
            file = await compressImage(file);
            const isImage = file.type.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
            const isVideo = file.type.startsWith("video/");
            if (isImage && selectedFiles.filter(item => item.type === "image").length + processed.filter(item => item.type === "image").length < 5) {
                processed.push({ file, src: URL.createObjectURL(file), type: "image" });
            } else if (isVideo && !selectedFiles.some(item => item.type === "video") && !processed.some(item => item.type === "video")) {
                processed.push({ file, src: URL.createObjectURL(file), type: "video" });
            }
        }
        setSelectedFiles(previous => [...previous, ...processed]);
        event.target.value = "";
    };

    const removeMedia = (index: number) => {
        setSelectedFiles(previous => {
            const file = previous[index];
            if (file) URL.revokeObjectURL(file.src);
            return previous.filter((_, itemIndex) => itemIndex !== index);
        });
    };

    const uploadCommentFiles = async (files: CommentFile[]) => {
        if (!files.length) return { imagesPaths: [] as string[], videoPath: null as string | null };

        const legacy = files.filter(file => file.type !== 'video');
        const imagesPaths: string[] = [];
        let videoPath: string | null = null;

        if (legacy.length) {
            const { data: uploadUrls } = await axios.post(
                `${REFLEXO_API_URL}/generate-upload-urls`,
                { files: legacy.map(file => ({ type: file.type, fileName: file.file.name, mimeType: file.file.type || 'application/octet-stream' })) },
                { headers: headers() },
            );
            if (!Array.isArray(uploadUrls) || uploadUrls.length !== legacy.length) throw new Error(t('generics.error'));
            await Promise.all(uploadUrls.map(async (upload: { url?: string; path?: string; type?: string }, index: number) => {
                if (!upload.url || !upload.path) throw new Error(t('generics.error'));
                await axios.put(upload.url, legacy[index].file, { headers: { 'Content-Type': legacy[index].file.type || 'application/octet-stream' } });
                if (upload.type === 'image') imagesPaths.push(upload.path);
            }));
        }

        const video = files.find(file => file.type === 'video');
        if (video) {
            const controller = new AbortController();
            videoUploadAbortRef.current = controller;
            const uploaded = await uploadResumableVideo({
                api: axios,
                apiBaseUrl: REFLEXO_API_URL,
                headers: headers(),
                file: video.file,
                signal: controller.signal,
                onProgress: setVideoUpload,
            });
            videoPath = uploaded.path;
            videoUploadAbortRef.current = null;
        }

        return { imagesPaths, videoPath };
    };

    const resetComposer = () => {
        selectedFiles.forEach(file => URL.revokeObjectURL(file.src));
        setText("");
        setGifUrl("");
        setSelectedFiles([]);
        setShowEmoji(false);
    };

    const handleSend = async () => {
        const value = text.trim();
        if ((!value && !gifUrl && !selectedFiles.length) || sending) return;
        setSending(true);
        try {
            const { imagesPaths, videoPath } = await uploadCommentFiles(selectedFiles);
            const payload: Record<string, unknown> = { text: value };
            if (gifUrl) payload.gif_url = gifUrl;
            if (imagesPaths.length) payload.images_paths = imagesPaths;
            if (videoPath) payload.video_path = videoPath;

            const { data } = await axios.post(
                `${REFLEXO_API_URL}/reels/${reelId}/comments`,
                payload,
                { headers: headers() },
            );
            setComments((prev) => [data.comment ?? data, ...prev]);
            setCommentCount((count) => count + 1);
            resetComposer();
        } catch (err: any) {
            setMessage(errorMessage(err) ?? t("generics.error"), "error");
        } finally {
            videoUploadAbortRef.current = null;
            setVideoUpload(null);
            setSending(false);
        }
    };

    const handlePinToggle = async (comment: ReelComment) => {
        const wasPinned = pinnedCommentIds.includes(comment.id) || Boolean(comment.is_pinned);
        if (!wasPinned && pinnedCommentIds.length >= 5) {
            setMessage(t("flow.comments.pinLimit", { defaultValue: "Você pode fixar até 5 comentários neste Flow." }), "error");
            return;
        }

        const previousPinnedIds = pinnedCommentIds;
        const nextPinnedIds = wasPinned
            ? previousPinnedIds.filter(id => id !== comment.id)
            : [...previousPinnedIds, comment.id];
        setPinnedCommentIds(nextPinnedIds);
        setComments(previous => sortPinnedComments(
            previous.map(item => item.id === comment.id ? { ...item, is_pinned: !wasPinned } : item),
            nextPinnedIds,
        ));

        try {
            const action = wasPinned ? "unpin" : "pin";
            const { data } = await axios.post(
                `${REFLEXO_API_URL}/reels/comments/${action}/${comment.id}`,
                {},
                { headers: headers() },
            );
            const serverPinnedIds = Array.isArray(data.pinned_comment_ids)
                ? data.pinned_comment_ids.map(Number).filter(Number.isFinite)
                : nextPinnedIds;
            setPinnedCommentIds(serverPinnedIds);
            setComments(previous => sortPinnedComments(previous, serverPinnedIds));
        } catch (err: any) {
            setPinnedCommentIds(previousPinnedIds);
            setComments(previous => sortPinnedComments(
                previous.map(item => item.id === comment.id ? { ...item, is_pinned: wasPinned } : item),
                previousPinnedIds,
            ));
            setMessage(errorMessage(err) ?? t("generics.error"), "error");
        }
    };

    const handleDelete = async (commentId: number) => {
        if (!window.confirm(t("flow.actions.confirmDelete"))) return;
        try {
            await axios.delete(`${REFLEXO_API_URL}/reels/comments/${commentId}`, { headers: headers() });
            setComments((prev) => prev.filter((comment) => comment.id !== commentId));
            setPinnedCommentIds((prev) => prev.filter(id => id !== commentId));
            setCommentCount((count) => Math.max(0, count - 1));
        } catch (err: any) {
            setMessage(errorMessage(err) ?? t("generics.error"), "error");
        }
    };

    return (
        <div className="reel-drawer-overlay" onClick={onClose}>
            <div className="reel-drawer" onClick={(e) => e.stopPropagation()}>
                <div className="reel-drawer-header">
                    <span className="reel-drawer-title">
                        {commentCount} {t("flow.comments.title")}
                    </span>
                    <button className="reel-drawer-close" onClick={onClose} aria-label={t("flow.actions.close")}>
                        <X size={18} />
                    </button>
                </div>

                <div className="reel-comments-list" ref={listRef} onScroll={handleScroll}>
                    {comments.map((comment) => {
                        const images = (comment.images ?? []) as ReelCommentMedia[];
                        return (
                            <div key={comment.id} className="reel-comment-row">
                                <img src={getImageUser(comment.user?.img_account ?? undefined)} alt={comment.user?.user_name} className="reel-comment-avatar" />
                                <div className="reel-comment-body">
                                    <span className="reel-comment-user">@{comment.user?.user_name}</span>
                                    {!!comment.text && <p className="reel-comment-text" dangerouslySetInnerHTML={{ __html: clearText(comment.text) }} />}
                                    {!!comment.gif_url && <img src={comment.gif_url} alt="GIF" className="reel-comment-gif" />}
                                    {!!images.length && (
                                        <div className="reel-comment-media-grid">
                                            {images.map((media, index) => (
                                                isVideoMedia(media) ? (
                                                    <video key={`${media.id}-${index}`} src={mediaUrl(media.path)} controls className="reel-comment-video" />
                                                ) : (
                                                    <img key={`${media.id}-${index}`} src={mediaUrl(media.path)} alt="" className="reel-comment-image" />
                                                )
                                            ))}
                                        </div>
                                    )}
                                    <div className="reel-comment-meta">
                                        {(pinnedCommentIds.includes(comment.id) || comment.is_pinned) && (
                                            <span className="reel-comment-pinned">
                                                <Pin size={12} /> {t("flow.comments.pinned", { defaultValue: "Fixado" })}
                                            </span>
                                        )}
                                        <span>{timeAgo(comment.created_at, t)}</span>
                                        {comment.can_pin && (
                                            <button className="reel-comment-action" onClick={() => handlePinToggle(comment)}>
                                                {pinnedCommentIds.includes(comment.id) || comment.is_pinned ? <PinOff size={13} /> : <Pin size={13} />}
                                                {pinnedCommentIds.includes(comment.id) || comment.is_pinned
                                                    ? t("flow.comments.unpin", { defaultValue: "Desafixar" })
                                                    : t("flow.comments.pin", { defaultValue: "Fixar" })}
                                            </button>
                                        )}
                                        {(comment.can_delete || comment.user?.id === user?.id) && (
                                            <button className="reel-comment-delete" onClick={() => handleDelete(comment.id)}>
                                                {t("flow.actions.delete")}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {!loading && !comments.length && (
                        <div className="reel-comments-empty">{t("flow.comments.empty")}</div>
                    )}

                    {loading && <div className="reel-comments-loading">{t("flow.comments.loading")}</div>}
                </div>

                <div className="reel-comment-composer" style={{ paddingBottom: keyboardPadding }}>
                    <EmojiPickerNative
                        show={showEmoji}
                        setShow={setShowEmoji}
                        onSelect={(emoji) => setText(previous => `${previous}${emoji}`)}
                        handleClickGif={url => setGifUrl(url)}
                        bottom={70 + keyboardPadding}
                    />
                    {videoUpload ? <div className="reel-comments-loading" role="status">{videoUpload.state === 'retrying' ? 'Reconectando o vídeo…' : 'Enviando vídeo…'} {Math.round((videoUpload.confirmedBytes / Math.max(videoUpload.totalBytes, 1)) * 100)}% · {formatUploadBytes(videoUpload.confirmedBytes)} / {formatUploadBytes(videoUpload.totalBytes)} <button type="button" onClick={() => videoUploadAbortRef.current?.abort()}>Cancelar</button></div> : null}
                    {!!selectedFiles.length && (
                        <div className="reel-comment-media-preview">
                            {selectedFiles.map((file, index) => (
                                <div className="reel-comment-media-preview-item" key={`${file.src}-${index}`}>
                                    {file.type === "video" ? <video src={file.src} controls /> : <img src={file.src} alt="" />}
                                    <button type="button" onClick={() => removeMedia(index)} aria-label={t("flow.actions.close")}><X size={13} /></button>
                                </div>
                            ))}
                        </div>
                    )}
                    {!!gifUrl && (
                        <div className="reel-comment-gif-preview">
                            <img src={gifUrl} alt="GIF" />
                            <button type="button" onClick={() => setGifUrl("")} aria-label={t("flow.actions.close")}><X size={13} /></button>
                        </div>
                    )}
                    <div className="reel-comment-input-row">
                        <img src={getImageUser(user?.img_account ?? undefined)} alt="" className="reel-comment-avatar" />
                        <div className="reel-comment-input-wrap">
                            <textarea
                                className="reel-comment-input"
                                placeholder={t("flow.comments.placeholder")}
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                                maxLength={500}
                                rows={1}
                            />
                            <div className="reel-comment-tools">
                                <button type="button" onClick={() => fileInputRef.current?.click()} aria-label={t("flow.comments.attach", { defaultValue: "Anexar fotos ou vídeos" })}>
                                    <Images size={18} />
                                </button>
                                <button type="button" onClick={() => setShowEmoji(previous => !previous)} aria-label={t("flow.comments.emoji", { defaultValue: "Emoji" })}>
                                    <Laugh size={18} />
                                </button>
                            </div>
                        </div>
                        <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" onChange={handleMediaChange} className="reel-comment-file-input" />
                        <button className="reel-send-btn" onClick={handleSend} disabled={(!text.trim() && !gifUrl && !selectedFiles.length) || sending} aria-label={t("flow.comments.send")}>
                            <Send size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
